import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ApprovalWorkflow, ApprovalWorkflowStatus } from '@/lib/api/types'
import {
  MoreVertical,
  Pencil,
  Trash2,
  Star,
  GitBranch,
  Layers,
  Archive,
  CheckCircle2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface WorkflowCardProps {
  workflow: ApprovalWorkflow
  onEdit: () => void
  onDelete: () => void
  onSetDefault: () => void
  onToggleStatus: () => void
}

export function WorkflowCard({
  workflow,
  onEdit,
  onDelete,
  onSetDefault,
  onToggleStatus,
}: WorkflowCardProps) {
  const getStatusColor = (status: ApprovalWorkflowStatus) => {
    switch (status) {
      case 'Active':
        return 'text-green-600 dark:text-green-400'
      case 'Draft':
        return 'text-amber-600 dark:text-amber-400'
      case 'Archived':
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  const getStatusBadge = (status: ApprovalWorkflowStatus) => {
    const styles: Record<ApprovalWorkflowStatus, string> = {
      Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
      Draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Archived: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
    }
    return <Badge className={cn('text-xs', styles[status])}>{status}</Badge>
  }

  const getStatusIcon = (status: ApprovalWorkflowStatus) => {
    switch (status) {
      case 'Active':
        return <CheckCircle2 className="h-5 w-5" />
      case 'Draft':
        return <GitBranch className="h-5 w-5" />
      case 'Archived':
        return <Archive className="h-5 w-5" />
    }
  }

  return (
    <Card className={cn(workflow.status === 'Archived' && 'opacity-60')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'p-2 rounded-lg',
                workflow.status === 'Active'
                  ? 'bg-neutral-100 dark:bg-neutral-800'
                  : 'bg-neutral-50 dark:bg-neutral-900'
              )}
            >
              <span className={getStatusColor(workflow.status)}>
                {getStatusIcon(workflow.status)}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{workflow.name}</CardTitle>
                {workflow.isDefault && (
                  <Badge variant="secondary" className="text-xs">
                    <Star className="h-3 w-3 mr-1" />
                    Default
                  </Badge>
                )}
              </div>
              {workflow.description && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {workflow.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {workflow.status !== 'Archived' && (
              <Switch
                checked={workflow.status === 'Active'}
                onCheckedChange={onToggleStatus}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {!workflow.isDefault && workflow.status === 'Active' && (
                  <DropdownMenuItem onClick={onSetDefault}>
                    <Star className="h-4 w-4 mr-2" />
                    Set as Default
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600 dark:text-red-400"
                  onClick={onDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          {getStatusBadge(workflow.status)}
          <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
            <Layers className="h-4 w-4" />
            <span>
              {workflow.stageCount} stage{workflow.stageCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <Button variant="outline" className="w-full mt-4" onClick={onEdit}>
          <Pencil className="h-4 w-4 mr-2" />
          Configure Stages
        </Button>
      </CardContent>
    </Card>
  )
}
